import { _ } from "../../../../../core/polyglot/polyglot.js";
import { spacer, stacker, text } from "../../../../../core/elements/index.js";
import AbstractForecastFiscalYearCardRenderingStrategy from "./AbstractForecastFiscalYearCardRenderingStrategy.js";

/**
 *	RenderingStrategy to render the ForecastFiscalYearCard for the
 *	Profit and Loss report tab.
 */
export default class IncomeStatementForecastFiscalYearCardRenderingStrategy extends AbstractForecastFiscalYearCardRenderingStrategy {
	constructor() {
		super(...arguments);

		this._predictionTimespan = undefined;
		this._fiscalYear = undefined;
		this._predictedValue = undefined;
		this._totalValue = undefined;
	}

	renderContentAsBigCardOn(html) {
		html.div(
			{
				"data-test": "ForecastFiscalYearCard",
				class: this._cssClass(),
			},
			spacer(
				{ direction: "vertical" },
				this._renderTotalOn.bind(this),
				this._renderPredictedValueOn.bind(this),
				this._renderPredictionTimespanOn.bind(this),
			),
		);
	}

	renderContentAsOneLinerOn(html) {
		stacker(
			{
				renderOn: html,
				dataTest: "ForecastFiscalYearCard",
				class: [this._cssClass(), "one-liner"],
				spacing: "between",
			},
			html.div(
				this._renderTitleOn.bind(this),
				this._renderFooterAsOneLinerOn.bind(this),
			),
			stacker({ isBlock: true }, this._renderValueOn.bind(this)),
		);
	}

	setValues({
		predictionTimespan,
		totalValue,
		predictedValue,
		fiscalYear,
	} = {}) {
		super.setValues(...arguments);

		this._predictionTimespan = predictionTimespan;
		this._totalValue = totalValue;
		this._predictedValue = predictedValue;
		this._fiscalYear = fiscalYear;
	}

	_getValue() {
		return this._totalValue;
	}

	_renderTotalOn(html) {
		html.div(
			{
				"data-test": "total",
				class: "total",
			},
			this._renderTitleOn.bind(this),
			this._renderValueOn.bind(this),
		);
	}

	_renderTitleOn(html) {
		const year = text(
			{ emphasize: true },
			this._renderFiscalYearOn.bind(this),
		);

		html.div(
			{ class: "title", "data-test": "title" },
			this._totalValue >= 0
				? _("Forecasted profit for {year}", { year })
				: _("Forecasted loss for {year}", { year }),
		);
	}

	_renderValueOn(html) {
		html.span(
			{ "data-test": "total-value", class: "value" },
			this._totalValueRenderer(),
		);
	}

	_renderFooterAsOneLinerOn(html) {
		const start = this._predictionTimespan?.getStart();
		const end = this._predictionTimespan?.getEnd();

		html.div(
			{ "data-test": "predicted" },
			_("(incl. {predictedValue} {timespan})", {
				predictedValue: this._predictedValueRenderer(),
				timespan: this._smartPeriodNameRenderer({ start, end }),
			}),
		);
	}

	_renderPredictedValueOn(html) {
		html.div(
			{
				"data-test": "predicted",
				class: [
					"predicted",
					"footer",
					{
						positive: this._predictedValue >= 0,
						negative: this._predictedValue < 0,
					},
				],
			},
			_("Incl. prediction"),
			spacer(
				{
					direction: "vertical",
					size: "small",
					align: "left",
				},
				html.span(
					{ "data-test": "predicted-value", class: "value" },
					this._predictedValueRenderer(),
				),
			),
		);
	}

	_totalValueRenderer() {
		return this._valueRenderer({ value: this._totalValue });
	}

	_predictedValueRenderer() {
		return this._valueRenderer({ value: this._predictedValue });
	}

	_renderPredictionTimespanOn(html) {
		if (!this._predictionTimespan) return;
		html.render(this._timespanRenderer(this._predictionTimespan));
	}

	_renderFiscalYearOn(html) {
		html.render(this._fiscalYear?.toSmartString() ?? "-");
	}
}
